import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { queryClient } from "..";
import { SqaureCheckBox } from "../components/checkbox/SquareCheckBox";
import { useSignInAdminMutation } from "../query/auth-query";
import { UserQueryKey, useGetMeQuery } from "../query/user-query";
import { PASSWORD_PLACEHOLDER, PHONE_PLACEHOLDER } from "../types/constant";
import { setLocalStorageAccessToken } from "../utils/local-storage/token";
import { setSessionStorageAccessToken } from "../utils/session-storage/token";

export const AuthView = () => {
  const initHelperText = "";
  const [signInPayload, setSignInPayload] = useState({
    username: "",
    password: "",
  });
  const [helperText, setHelperText] = useState(initHelperText);
  const [keepSignIn, setKeepSignIn] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const signInAdminMutation = useSignInAdminMutation();
  useGetMeQuery();

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSignInPayload({
      ...signInPayload,
      [event.target.name]: event.target.value,
    });
  };

  const handleClickKeepSignInCheckBox = () => {
    setKeepSignIn(!keepSignIn);
  };

  const handleClickSignInButton = async () => {
    signInAdminMutation.mutate(signInPayload, {
      onSuccess: ({ data }) => {
        if (keepSignIn) {
          setLocalStorageAccessToken(data.accessToken);
        } else {
          setSessionStorageAccessToken(data.accessToken);
        }
        queryClient.invalidateQueries(UserQueryKey.GetMe);
      },
      onError: (error) => {
        console.log(error);
        setHelperText("아이디 또는 비밀번호를 확인해주세요");
      },
    });
  };

  const handlePressedEnter = (event: any) => {
    if (event.key === "Enter") {
      handleClickSignInButton();
    }
  };

  useEffect(() => {
    document.addEventListener("keypress", handlePressedEnter);
    return () => {
      document.removeEventListener("keypress", handlePressedEnter);
    };
  }, [signInPayload]);

  const clearPlaceholder = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.placeholder = "";
  };

  const resetPlaceholder =
    (placeholder: string) => (event: React.FocusEvent<HTMLInputElement>) => {
      event.target.placeholder = placeholder;
    };

  return (
    <AuthContainer ref={ref}>
      <AuthFormWrapper>
        <AuthFormContainer>
          <AuthFormLogo
            src={process.env.PUBLIC_URL + "/assets/image/img_onggoing.png"}
          />
          <AuthFormTitle>
            <AuthFormTitleLabel>옹고잉 실시간 배송조회</AuthFormTitleLabel>
          </AuthFormTitle>
          <AuthTextField
            placeholder={PHONE_PLACEHOLDER}
            name="username"
            value={signInPayload.username}
            onChange={handleChangeTextField}
            onFocus={clearPlaceholder}
            onBlur={resetPlaceholder(PHONE_PLACEHOLDER)}
          />
          <AuthTextField
            placeholder={PASSWORD_PLACEHOLDER}
            name="password"
            type="password"
            value={signInPayload.password}
            onChange={handleChangeTextField}
            onFocus={clearPlaceholder}
            onBlur={resetPlaceholder(PASSWORD_PLACEHOLDER)}
          />
          {helperText && (
            <AuthFormHelperTextContainer>
              <AuthFormHelperTextLabel>{helperText}</AuthFormHelperTextLabel>
            </AuthFormHelperTextContainer>
          )}
          <AuthFormButton onClick={handleClickSignInButton}>
            로그인
          </AuthFormButton>
          <KeepSignInContainer onClick={handleClickKeepSignInCheckBox}>
            <KeepSignInCheckBoxWrapper>
              <SqaureCheckBox checked={keepSignIn} fill="#000000" size={24} />
            </KeepSignInCheckBoxWrapper>
            <KeepSignInLabel>로그인 상태 유지</KeepSignInLabel>
          </KeepSignInContainer>
        </AuthFormContainer>
      </AuthFormWrapper>
      <AuthFormContactContainer>
        <AuthFormContactLabel>관리자 문의 | 070-8064-3647</AuthFormContactLabel>
      </AuthFormContactContainer>
    </AuthContainer>
  );
};

const AuthContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #ebeef3;
`;

const AuthFormWrapper = styled.div`
  width: 646px;
  height: 457px;
  display: flex;
  box-sizing: border-box;
  padding: 53px 138px 71px 138px;
  background: #ffffff;
  border: 1px solid #e0e3e9;
  border-radius: 8px;
`;

const AuthFormContainer = styled.div`
  width: 368px;
  height: 363px;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AuthFormLogo = styled.img`
  width: 100px;
  height: 60px;
`;

const AuthFormTitle = styled.div`
  width: 368px;
  height: 48px;
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 48px;
  display: flex;
  align-items: center;

  color: #000000;
  margin: 0 0 12px 0;
  box-sizing: border-box;
`;

const AuthFormTitleLabel = styled.h2`
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  text-align: center;
  vertical-align: middle;
`;

const AuthTextField = styled.input`
  width: 368px;
  height: 42px;
  margin: 12px 0 0 0;
  border: 1px solid #e0e3e9;
  border-radius: 4px;
  background: #f5f6f9;
  font-size: 14px;
  font-weight: 400;
  padding: 11px;
  box-sizing: border-box;
  outline: none;
  &::placeholder {
    color: #979da8;
  }
  &:focus {
    border: 1px solid #fcca00;
  }
`;

const AuthFormButton = styled.button`
  margin: 20px 0 16px 0;
  padding: 0;

  width: 368px;
  height: 43px;

  background: #1c2026;
  border-radius: 4px;

  border: none;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  text-align: center;

  color: #fcca00;
  cursor: pointer;
`;

const AuthFormHelperTextContainer = styled.div`
  width: 367px;
  height: 32px;
`;

const AuthFormHelperTextLabel = styled.div`
  width: 100%;
  height: 100%;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  vertical-align: bottom;
  box-sizing: border-box;
  padding: 12px 0 0 12px;
  color: #f94d4d;
`;

const KeepSignInContainer = styled.div`
  width: 368px;
  height: 24px;
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const KeepSignInCheckBoxWrapper = styled.div`
  width: 24px;
  height: 24px;
  margin: 0 16px 0 0;
  padding: 0;
`;

const KeepSignInLabel = styled.div`
  font-weight: 400;
  font-size: 13px;
  line-height: 24px;
  color: #4d535c;
  text-align: center;
  vertical-align: middle;
  -ms-user-select: none;
  -moz-user-select: -moz-none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  user-select: none;
`;

const AuthFormContactContainer = styled.div`
  width: 645px;
  height: 81px;
`;

const AuthFormContactLabel = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  vertical-align: middle;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #4d535c;
`;
